import React, { useState } from 'react';
import { func, string, bool } from 'prop-types';
import { validate as validator } from 'ui-validator';
import { CurrencyInput, FAQNote } from '@shared_modules/local-propulsion/components';
import { heapTrackErrors, heapTrackEvent } from '@shared_modules/baui-heap-tracking';
import validations from './income-validations';
import { Page } from '../../components';
import { Grid } from 'propulsion-rc-v2';
import { submitHeapTracking, truncateLongName } from '../../utils/util';
import { warningText, title, header, inputLabel, minIncome } from './constants';

const alimonyTextA = 'Alimony, child support, and maintenance income are optional.';
const alimonyTextB = 'Alimony, child support, or separate maintenance income are optional.';

const defaultLabelProps = ({ firstName, isJoint, isSecondary }) => {
  const common = {
    warningText,
    checkboxText: 'I have additional income',
    inputLabel: 'Total Individual Income',
  };

  if (isSecondary) {
    const truncateName = truncateLongName(firstName);
    return {
      ...common,
      title: `${truncateName}, how much do you make each year?`,
      header: 'By giving us a clearer picture of your finances, we can maximize your offer.',
      helperText: '',
    };
  }

  return {
    ...common,
    title: `How much do you make each year?`,
    header: 'By giving us a clearer picture of your finances, we can maximize your offer.',
    helperText:
      'If you plan on applying with a co-borrower, only enter your income here. We’ll ask for your co-borrower’s income later.',
  };
};

const incomeQuestionALabelProps = ({ firstName, isJoint, isSecondary }) => {
  const common = {
    inputLabel,
  };
  if (isSecondary)
    return {
      ...common,
      title: `${truncateLongName(firstName)}, what’s your total annual income, before taxes?`,
      header,
    };

  return {
    ...common,
    title,
    header: isJoint
      ? 'Only include your income. We’ll ask for your co-borrower’s income later. Understanding your total income helps us personalize your loan offer.'
      : header,
  };
};

const Income = ({
  income: initialIncome,
  isSecondary,
  firstName,
  isJoint,
  saveValues,
  nextLocation,
  previousLocation,
  isDM,
  isFromSecondLoanFlow,
}) => {
  const [income, setIncome] = useState(initialIncome);
  const [showWarningText, setShowWarningText] = useState(initialIncome && initialIncome < minIncome);
  const [validationErrors, setValidationErrors] = useState({});
  const [isFaqNoteOpened, setIsFaqNoteOpened] = useState(true);

  const getPageProps = () => {
    const commonContext = { firstName, isJoint, isSecondary };
    let labelProps = defaultLabelProps({
      ...commonContext,
      ...incomeQuestionALabelProps(commonContext),
    });
    labelProps = { ...labelProps, ...incomeQuestionALabelProps(commonContext) };
    return labelProps;
  };

  const handleFocus = () => {
    const hasErrors = Object.keys(validationErrors).length > 0;
  };

  const handleBlur = () => {
    const errors = validator({ income }, validations);
    setShowWarningText(income && income < minIncome);
    setValidationErrors(errors);
  };

  const trackSubmitData = () => {
    let eventProps = {
      APP_TYPE: isJoint ? 'joint' : 'individual',
      INCOME_TYPE: isSecondary ? 'CO-BORROWER' : 'PRIMARY BORROWER',
      INCOME: income,
      FAQ_NOTE_STATE: isFaqNoteOpened ? 'OPEN' : 'CLOSED',
    };
    heapTrackEvent('Income_CTA_Click', eventProps);
    if (isSecondary) submitHeapTracking(isJoint);
  };

  const submit = e => {
    if (e) e.preventDefault();
    const errors = validator({ income }, validations);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowWarningText(false);
      heapTrackErrors(errors);
    } else {
      const type = isSecondary || isSecondary ? 'secondary' : 'primary';
      saveValues({ income: { income } }, type);
      trackSubmitData();
      nextLocation();
    }
  };

  const { inputLabel, helperText, warningText, checkboxText, ...pageProps } = getPageProps();

  return (
    <Page name="income" goBack={previousLocation} submitHandler={submit} {...pageProps}>
      <div className="form__input">
        <CurrencyInput
          name="income"
          label={inputLabel}
          onBlur={handleBlur}
          defaultValue={income}
          onChange={event => setIncome(event.target.rawValue)}
          onFocus={handleFocus}
          errors={validationErrors.income}
          warningText={showWarningText ? warningText : ''}
          helperText={isFromSecondLoanFlow || isDM ? '' : helperText}
          autoFocus={!income}
          aria-invalid={Object.keys(validationErrors).length > 0 && !!validationErrors.income}
          allowDecimals
        />
      </div>
      <Grid container justifyContent="center">
        <Grid item xs={12}>
          <FAQNote onChange={() => setIsFaqNoteOpened(!isFaqNoteOpened)} opened={isFaqNoteOpened} />
        </Grid>
      </Grid>
    </Page>
  );
};

Income.propTypes = {
  saveValues: func.isRequired,
  previousLocation: func.isRequired,
  nextLocation: func.isRequired,
  firstName: string,
  isSecondary: bool.isRequired,
  isJoint: bool.isRequired,
  income: string,
  onIncomeAdditionalClick: func.isRequired,
};

Income.defaultProps = {
  income: '',
  firstName: '',
};

export default Income;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pages/Income/Income.scss');